import { animateItemsIn } from './modules/animate-items-in.js';

/**
 * Podcasts Section: "Load more" AJAX pagination + inline video facade
 * player. Mirrors events-ajax.js's own bind-and-rebind approach (ACF's
 * ServerSideRender / editor re-renders replace the whole grid/button pair
 * with brand new DOM nodes, so this keeps watching for the page's lifetime
 * instead of binding once and stopping).
 */
export const initPodcastsAjax = () => {
    const bound = new WeakSet();

    const bindIfNew = () => {
        const grid = document.querySelector('.js-podcasts-grid');
        const loadBtn = document.querySelector('.js-load-podcasts');
        if (grid && !bound.has(grid)) {
            bound.add(grid);
            bindPodcastsGrid(grid, loadBtn);
        }
    };

    bindIfNew();

    const observer = new MutationObserver(bindIfNew);
    observer.observe(document.body, { childList: true, subtree: true });
};

function bindPodcastsGrid(grid, loadBtn) {
    // Delegated on the grid itself (not per-card) so newly AJAX-inserted
    // cards work without any rebinding.
    grid.addEventListener('click', (e) => {
        const playBtn = e.target.closest('.js-podcast-play');
        if (!playBtn) return;

        const card = playBtn.closest('.podcast-video-card');
        const videoId = card?.dataset.videoId;
        const media = card?.querySelector('.podcast-video-card__media');
        if (!videoId || !media) return;

        const title = card.querySelector('.podcast-video-card__title')?.textContent || '';
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1`;
        iframe.title = title;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.loading = 'lazy';
        media.replaceChildren(iframe);
        // The button just removed (playBtn) may have held keyboard focus --
        // without moving it explicitly, a keyboard user who activated play
        // via Enter/Space loses focus to <body> with no indication where it
        // went. Iframes are natively focusable with no tabindex needed.
        iframe.focus();
    });

    if (!loadBtn) return;

    const loadMoreWrap = loadBtn.closest('.podcasts-section__load-more');
    const errorEl = loadMoreWrap?.querySelector('.js-podcasts-load-error');
    const statusEl = loadMoreWrap?.querySelector('.js-podcasts-load-status');

    const showLoadError = (message) => {
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
    };
    const clearLoadError = () => {
        if (!errorEl) return;
        errorEl.hidden = true;
        errorEl.textContent = '';
    };
    const announce = (message) => {
        if (statusEl) statusEl.textContent = message;
    };

    const getRealCardCount = () =>
        grid.querySelectorAll('.podcast-video-card:not(.podcast-video-card--test)').length;

    const getDevice = () => {
        if (window.innerWidth < 480) return 'mobile';
        if (window.innerWidth < 1024) return 'tablet';
        return 'desktop';
    };

    // Grid is ACF-configurable per breakpoint (grid_{desktop,tablet,mobile}_
    // columns/rows), so cols/rows come from the grid's own data attributes
    // (section-templates/podcasts_section.php), not from reading computed
    // CSS grid-template-columns back out the way events-ajax.js does.
    const getColsRows = () => {
        const device = getDevice();
        const suffix = device[0].toUpperCase() + device.slice(1);
        const cols = parseInt(grid.dataset[`cols${suffix}`] || '', 10);
        const rows = parseInt(grid.dataset[`rows${suffix}`] || '', 10);
        return {
            cols: Number.isFinite(cols) && cols > 0 ? cols : 3,
            rows: Number.isFinite(rows) && rows > 0 ? rows : 2,
        };
    };

    loadBtn.addEventListener('click', async (e) => {
        e.preventDefault();

        if (loadBtn.disabled) return;
        loadBtn.disabled = true;

        try {
            const { cols, rows } = getColsRows();
            const offset = getRealCardCount();
            const total = parseInt(loadBtn.dataset.total || '0', 10);

            if (offset >= total) {
                loadBtn.hidden = true;
                return;
            }

            loadBtn.classList.add('loading');
            clearLoadError();
            const formData = new FormData();
            formData.append('action', 'load_more_podcasts');
            formData.append('offset', offset);
            formData.append('cols', cols);
            formData.append('rows', rows);
            formData.append('source_url', grid.dataset.sourceUrl || '');
            formData.append('search_query', grid.dataset.searchQuery || '');
            formData.append('sort_order', grid.dataset.sortOrder || 'date');
            formData.append('pinned', grid.dataset.pinned || '[]');
            formData.append('nonce', window.jctc_vars?.nonce || '');

            try {
                const response = await fetch(window.jctc_vars.ajax_url, {
                    method: 'POST',
                    body: formData,
                });
                const result = await response.json();

                if (result.success) {
                    const addedCount = result.data.html
                        ? (() => {
                              const realBefore = getRealCardCount();
                              const allBefore = grid.querySelectorAll('.podcast-video-card').length;
                              grid.insertAdjacentHTML('beforeend', result.data.html);
                              animateItemsIn(
                                  Array.from(grid.querySelectorAll('.podcast-video-card')).slice(
                                      allBefore
                                  )
                              );
                              return getRealCardCount() - realBefore;
                          })()
                        : 0;
                    loadBtn.dataset.total = result.data.total || loadBtn.dataset.total || '0';
                    if (getRealCardCount() >= parseInt(loadBtn.dataset.total || '0', 10)) {
                        loadBtn.hidden = true;
                    }
                    announce(
                        addedCount > 0
                            ? `${addedCount} more video${addedCount === 1 ? '' : 's'} loaded.`
                            : 'No more videos to load.'
                    );
                } else {
                    // Server responded but declined the request (e.g. rate
                    // limit, invalid nonce) -- previously this just silently
                    // re-enabled the button with no explanation.
                    showLoadError(
                        result.data?.message || "Couldn't load more videos. Please try again."
                    );
                }
            } catch (error) {
                console.error('Podcasts AJAX load error:', error);
                showLoadError(
                    "Couldn't load more videos. Please check your connection and try again."
                );
            } finally {
                loadBtn.classList.remove('loading');
            }
        } finally {
            loadBtn.disabled = false;
        }
    });
}
